import { Modal } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import type { VoidFunctionComponent } from 'react';

import { pluralizeIssues } from '../../../../shared/helpers/plural';
import { useAddToast } from '../../../contexts/ToastContext';
import * as IssuesV2Store from '../../../redux/issuesV2';
import Button from '../../generic/Button/Button';
import { BUTTON_SIZE, BUTTON_TYPE } from '../../generic/Button/button-constants';
import ButtonGroup from '../../generic/Button/ButtonGroup';
import InlineLoaderButton from '../../generic/Button/InlineLoaderButton';
import { ErrorAlert } from '../../generic/ErrorAlert';
import { SELECTED_ISSUE_COUNT } from '../IssueDetails/ActionButtons/shared';

import useClearErrorsOnUnmount from './hooks/useClearErrorsOnUnmount';

type UnlinkIssuesModalBaseProps = {
  show: boolean;
  onHide: () => void;
};

type UnlinkIssuesModalProps = UnlinkIssuesModalBaseProps & {
  text: string;
  onUnlink: () => void;
};

/**
 * UnlinkIssuesModal represents the UI of the modal for unlinking issues.
 */
const UnlinkIssuesModal: VoidFunctionComponent<UnlinkIssuesModalProps> = ({
  text,
  show,
  onHide,
  onUnlink,
}) => {
  const { loading: isLoading, errors } = useSelector(IssuesV2Store.selectors.getAsyncStatusUpdateIssues);

  //  Ensure that when the component unmounts, we remove any error messages
  useClearErrorsOnUnmount();

  return (
    <Modal
      backdrop="static"
      animation
      show={show}
      onHide={onHide}
      onEscapeKeyDown={onHide}
      className="issues-modal"
    >
      <Modal.Header bsClass="issues-modal__header">
        <Modal.Title bsClass="issues-modal__header__title">
          Unlink
        </Modal.Title>
      </Modal.Header>
      <Modal.Body bsClass="issues-modal__body">
        <span className="issues-modal__body__message">
          {text}
        </span>
        {errors &&
          <div className="issues-modal__body__errors">
            <ErrorAlert status={{ errors }} />
          </div>}
      </Modal.Body>
      <Modal.Footer bsClass="issues-modal__footer">
        <ButtonGroup>
          <Button
            type={BUTTON_TYPE.SECONDARY}
            size={BUTTON_SIZE.MEDIUM}
            isDisabled={isLoading}
            onClick={onHide}
          >
            Cancel
          </Button>
          <InlineLoaderButton
            type={BUTTON_TYPE.PRIMARY}
            size={BUTTON_SIZE.MEDIUM}
            isSubmitButton
            isLoading={isLoading}
            isDisabled={isLoading}
            onClick={onUnlink}>
            Unlink
          </InlineLoaderButton>
        </ButtonGroup>
      </Modal.Footer>
    </Modal>
  );
};

/**
 * Container component for the unlink modal for use in the IssuesInbox.
 * For the inbox, after unlinking we need to re-fetch the issues.
 */
export const UnlinkIssuesModalInboxContainer: VoidFunctionComponent<UnlinkIssuesModalBaseProps> = ({
  show,
  onHide,
}) => {
  const addToast = useAddToast();
  const dispatch = useDispatch();
  const selectedIssuesCount = useSelector(IssuesV2Store.selectors.getSelectedIssuesCount);

  const onUnlink = async () => {
    await dispatch(IssuesV2Store.actions.unlinkSelectedIssues());

    onHide();
    addToast({
      type: 'success',
      content: `Unlinked ${pluralizeIssues(selectedIssuesCount)}`,
    });
  };

  return (
    <UnlinkIssuesModal
      text="All selected issues will no longer be associated with a Jira ticket."
      show={show}
      onHide={onHide}
      onUnlink={onUnlink}
    />
  );
};

type UnlinkIssuesModalDetailsProps = UnlinkIssuesModalBaseProps & {
  id: number;
  //  In the global issue details, there could be multiple issues which are ticketd.
  //  For this case, we need to update the text to say "n issues".
  jiraTicketCount?: number;
};

/**
 * Container component for the unlink modal for use in the IssueDetails.
 */
export const UnlinkIssuesModalDetailsContainer: VoidFunctionComponent<UnlinkIssuesModalDetailsProps> = ({
  id,
  show,
  onHide,
  jiraTicketCount = SELECTED_ISSUE_COUNT,
}) => {
  const addToast = useAddToast();
  const dispatch = useDispatch();

  const text = jiraTicketCount === 1
    ? 'This issue will no longer be associated with this Jira ticket.'
    : `These ${jiraTicketCount} Jira tickets will no longer be associated with this issue.`;

  const onUnlink = async () => {
    //  We need the action to hit the backend before we re-fetch the status summary.
    await dispatch(IssuesV2Store.actions.unlinkIssues({ issueIds: [id] }));

    //  This handles either closing the drawer (if on the inbox) or
    //  re-fetching the issue details (if we're on the full-page).
    dispatch(IssuesV2Store.actions.refetchIssueDetailsAfterAction(id));

    onHide();
    addToast({
      type: 'success',
      content: `Unlinked issue.`,
    });
  };

  return (
    <UnlinkIssuesModal
      text={text}
      show={show}
      onHide={onHide}
      onUnlink={onUnlink}
    />
  );
};
